import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Users, CreditCard, Search, Filter, Eye, Calendar,
  Crown, LogOut, ChevronRight, ChevronLeft, Mail, MapPin,
  Loader2, BarChart3, UserCheck, Clock, Trash2, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  
  const limit = 20;

  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      
      if (filter !== 'all') params.append('filter_type', filter);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/api/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [token, page, filter, search, navigate, logout]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchUserDetail = async (userId) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetail(response.data);
    } catch (err) {
      console.error('Failed to fetch user detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(0);
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    fetchUserDetail(user.id);
  };

  const totalPages = Math.ceil(total / limit);

  // Delete single user
  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Er du sikker på at du vil slette ${userName}?`)) return;
    
    setDeleteLoading(userId);
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from list
      setUsers(users.filter(u => u.id !== userId));
      setTotal(total - 1);
      alert('Bruger slettet!');
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Kunne ikke slette bruger: ' + (err.response?.data?.detail || 'Ukendt fejl'));
    } finally {
      setDeleteLoading(null);
    }
  };

  // Cleanup all test users
  const handleCleanupTestUsers = async () => {
    if (!window.confirm('Er du sikker på at du vil slette ALLE testbrugere?\n\nDette sletter brugere med:\n- @test.dk emails\n- @example.com emails\n- "Test User" navne')) return;
    
    setCleanupLoading(true);
    setCleanupResult(null);
    try {
      const response = await axios.delete(`${API}/api/admin/cleanup/test-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCleanupResult(response.data);
      // Refresh user list
      fetchUsers();
    } catch (err) {
      console.error('Failed to cleanup test users:', err);
      alert('Kunne ikke slette testbrugere: ' + (err.response?.data?.detail || 'Ukendt fejl'));
    } finally {
      setCleanupLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Flirra Admin</h1>
              <p className="text-xs text-slate-400">Brugere</p>
            </div>
          </div>
          
          <nav className="flex items-center gap-2">
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                <BarChart3 className="w-4 h-4 mr-2" />
                Oversigt
              </Button>
            </Link>
            <Link to="/admin/users">
              <Button variant="ghost" size="sm" className="text-white bg-slate-700/50">
                <Users className="w-4 h-4 mr-2" />
                Brugere
              </Button>
            </Link>
            <Link to="/admin/payments">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                <CreditCard className="w-4 h-4 mr-2" />
                Betalinger
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={logout}
              className="text-slate-400 hover:text-red-400 ml-4"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Søg efter navn eller email..."
                className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="admin-user-search"
              />
            </div>
            <Button type="submit" className="bg-primary hover:bg-primary/90">
              Søg
            </Button>
          </form>
          
          <Select value={filter} onValueChange={(v) => { setFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-white" data-testid="admin-user-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle brugere</SelectItem>
              <SelectItem value="premium">Premium</SelectItem>
              <SelectItem value="free">Gratis</SelectItem>
              <SelectItem value="trial">Prøveperiode</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results count and cleanup button */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-slate-400 text-sm">
            Viser {users.length} af {total} brugere
            {search && <span> for "{search}"</span>}
          </p>
          <Button 
            onClick={handleCleanupTestUsers}
            disabled={cleanupLoading}
            variant="destructive"
            size="sm"
            className="bg-red-600 hover:bg-red-700"
            data-testid="cleanup-test-users-btn"
          >
            {cleanupLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Slet alle testbrugere
          </Button>
        </div>

        {/* Cleanup Result */}
        {cleanupResult && (
          <div className="mb-4 p-4 bg-green-900/30 border border-green-700/50 rounded-lg">
            <h4 className="text-green-400 font-semibold mb-2">✅ Testbrugere slettet!</h4>
            <div className="text-sm text-slate-300 space-y-1">
              <p>Brugere slettet: {cleanupResult.deleted_users}</p>
              <p>Swipes slettet: {cleanupResult.deleted_swipes}</p>
              <p>Matches slettet: {cleanupResult.deleted_matches}</p>
              <p>Beskeder slettet: {cleanupResult.deleted_messages}</p>
            </div>
          </div>
        )}

        {/* Users Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Bruger</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Email</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Oprettet</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Handling</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-700/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-700 overflow-hidden flex-shrink-0">
                            {user.photos?.[0] ? (
                              <img 
                                src={user.photos[0].startsWith('http') ? user.photos[0] : `${API}${user.photos[0]}`} 
                                alt={user.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-slate-500">
                                {user.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.name || 'Ukendt'}</p>
                            {user.location && (
                              <p className="text-slate-400 text-xs flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {user.location}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-300 text-sm">{user.email}</span>
                      </td>
                      <td className="px-4 py-3">
                        {user.is_premium ? (
                          <Badge className="bg-amber-500/20 text-amber-400 border-0">
                            <Crown className="w-3 h-3 mr-1" />
                            {user.premium_tier === 'platinum' ? 'Platin' : user.premium_tier === 'gold' ? 'Guld' : 'Basis'}
                          </Badge>
                        ) : user.trial_used ? (
                          <Badge className="bg-blue-500/20 text-blue-400 border-0">
                            <Clock className="w-3 h-3 mr-1" />
                            Trial brugt
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-500/20 text-slate-400 border-0">
                            Gratis
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-400 text-sm">{formatDate(user.created_at)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUserClick(user)}
                            className="text-slate-400 hover:text-white"
                            data-testid={`view-user-${user.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Se
                          </Button>
                          {!user.is_admin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id, user.name || user.email)}
                              disabled={deleteLoading === user.id}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`delete-user-${user.id}`}
                            >
                              {deleteLoading === user.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700/50">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="text-slate-400 hover:text-white"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Forrige
                </Button>
                <span className="text-slate-400 text-sm">
                  Side {page + 1} af {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="text-slate-400 hover:text-white"
                >
                  Næste
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* User Detail Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Brugerdetaljer</DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : userDetail && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-slate-700 overflow-hidden flex-shrink-0">
                  {userDetail.user.photos?.[0] ? (
                    <img 
                      src={userDetail.user.photos[0].startsWith('http') ? userDetail.user.photos[0] : `${API}${userDetail.user.photos[0]}`} 
                      alt={userDetail.user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-500 text-xl">
                      {userDetail.user.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{userDetail.user.name}</h3>
                  <p className="text-slate-400 text-sm">{userDetail.user.email}</p>
                  {userDetail.user.is_premium && (
                    <Badge className="bg-amber-500/20 text-amber-400 border-0 mt-1">
                      <Crown className="w-3 h-3 mr-1" />
                      {userDetail.user.premium_tier === 'platinum' ? 'Platin' : userDetail.user.premium_tier === 'gold' ? 'Guld' : 'Basis'}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Matches</p>
                  <p className="text-xl font-semibold">{userDetail.stats.matches}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Beskeder sendt</p>
                  <p className="text-xl font-semibold">{userDetail.stats.messages_sent}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Likes givet</p>
                  <p className="text-xl font-semibold">{userDetail.stats.likes_given}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Likes modtaget</p>
                  <p className="text-xl font-semibold">{userDetail.stats.likes_received}</p>
                </div>
              </div>

              {/* Purchase Stats */}
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Købshistorik
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                    <p className="text-green-400 text-xs">Total brugt</p>
                    <p className="text-lg font-bold text-green-400">
                      {userDetail.purchase_stats?.total_spent || 0} DKK
                    </p>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                    <p className="text-amber-400 text-xs">Boosts købt</p>
                    <p className="text-lg font-bold text-amber-400">
                      {userDetail.purchase_stats?.boost_purchases || 0}
                    </p>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 text-center">
                    <p className="text-purple-400 text-xs">Abonnementer</p>
                    <p className="text-lg font-bold text-purple-400">
                      {userDetail.purchase_stats?.subscription_purchases || 0}
                    </p>
                  </div>
                </div>
                
                {/* Recent Transactions */}
                {userDetail.transactions && userDetail.transactions.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-slate-400 mb-2">Seneste transaktioner:</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {userDetail.transactions.slice(0, 5).map((t, i) => (
                        <div key={i} className="flex items-center justify-between text-xs bg-slate-700/30 rounded px-2 py-1">
                          <span className="text-slate-300">
                            {t.type === 'boost' ? '🚀 Boost' : t.type === 'subscription' ? '👑 Abonnement' : '🎁 Trial'}
                          </span>
                          <span className="text-green-400 font-medium">{t.amount} DKK</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* User Details */}
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Alder</span>
                  <span>{userDetail.user.age || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Køn</span>
                  <span>{userDetail.user.gender === 'male' ? 'Mand' : userDetail.user.gender === 'female' ? 'Kvinde' : '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Lokation</span>
                  <span>{userDetail.user.location || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Oprettet</span>
                  <span>{formatDate(userDetail.user.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Email verificeret</span>
                  <span>{userDetail.user.email_verified ? 'Ja' : 'Nej'}</span>
                </div>
              </div>

              {/* Payments */}
              {userDetail.payments?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-400 mb-2">Betalinger</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {userDetail.payments.map((payment, i) => (
                      <div key={i} className="bg-slate-700/50 rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <p className="text-sm">{payment.description || payment.type}</p>
                          <p className="text-xs text-slate-400">{formatDate(payment.created_at)}</p>
                        </div>
                        <span className="text-emerald-400 font-medium">{payment.amount} DKK</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
